import type { LoraModel, LoraListResponse, LoraCreateRequest } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

class LoraAPI {
  private authTokenGetter: (() => string | null) | null = null;

  setAuthTokenGetter(getter: () => string | null) {
    this.authTokenGetter = getter;
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = this.authTokenGetter?.();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async list(): Promise<LoraListResponse> {
    const response = await fetch(`${API_BASE}/api/v1/loras`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to fetch models' }));
      throw new Error(error.detail || `Error: ${response.status}`);
    }

    return response.json();
  }

  async create(data: LoraCreateRequest): Promise<LoraModel> {
    const response = await fetch(`${API_BASE}/api/v1/loras`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to create model' }));
      throw new Error(error.detail || `Error: ${response.status}`);
    }

    return response.json();
  }

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/api/v1/loras/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete model' }));
      throw new Error(error.detail || `Error: ${response.status}`);
    }
  }

  async setDefault(id: string): Promise<LoraModel> {
    const response = await fetch(`${API_BASE}/api/v1/loras/${id}/set-default`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to set default' }));
      throw new Error(error.detail || `Error: ${response.status}`);
    }

    return response.json();
  }
}

export const loraApi = new LoraAPI();

